import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { css } from "styled-components"
import { Grid, Block, Spacer } from "../../atoms/layout-primitives"
import Img from "gatsby-image"
import ContentBlock from "../../molecules/content-block"
import ReactPlayer from "react-player"
import Button from "../../atoms/button"

export default function Overview() {
  const query = useStaticQuery(graphql`
    query {
      gridImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/overview/who needs IPS.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      positioningImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/overview/positioning illustration.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mappingImage: file(
        relativePath: {
          eq: "1_SOLUTIONS/Mapping CMS - Internal Pages/Indoor Positioning/overview/mapping illustration.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  return (
    <>
      <Grid>
        {/* What is an indoor positioning system */}
        <Spacer 
          height={{ sm: "32px", md: "64px", lg: "96px" }} 
          row={{ sm: "1" }}
        />
        <Block 
            col={{ sm: "1 / -1", md: "4 / -4", lg: "2 / 6" }}
            row={{ sm: "2" }}
            variants={[
                css`
                    width: 100%;
                    height: calc(1.186 * 91.11vw); // first number is ratio of height:width, second is width:display 
                `,
                css`
                    height: calc(1.186 * 45.96vw);
                `,
                css`
                    height: calc(1.186 * 30.76vw);
                    max-width: 443px;
                    max-height: 526px;
                `
            ]}
        >
          <ReactPlayer
            url={"https://vimeo.com/488132212/617c75518c"}
            playing={true}
            loop={true}
            volume={0}
            muted={true}
            controls={false}
            width={"100%"}
            height={"100%"}
            playsinline={true}
          />
        </Block>
        <Block 
            col={{ sm: "1 / -1", md: "2 / -2", lg: "7 / 12" }}
            row={{ sm: "3", md: "3", lg: "2" }}
            variants={[
                css`
                    margin-top: 48px;
                    text-align: center;
                `,
                css``,
                css`
                    margin-top: 0px;
                    align-self: center;
                    text-align: left;
                `
            ]}
        >
          <ContentBlock.H2
            title="What is an Indoor Positioning System (IPS)?"
            description="An indoor positioning system (IPS) can be thought of as GPS for indoor venues. Using a variety of methods, these systems can detect real-time locations to determine the coordinates of people or assets inside a building. These coordinates are typically represented visually by a blue dot on a digital indoor map to provide additional context to users and features such as wayfinding."
            bodyVariants={[
              css`
                color: var(--body-text-grey);
              `,
            ]}
          />
        </Block>

        {/* Who needs indoor positioning systems */}
        <Spacer 
          height={{ sm: "64px", md: "96px", lg: "96px" }}
          row={{ sm: "4", md: "4", lg: "3" }}
        />
        <Block 
          col={{ sm: "1 / -1", md: "3 / -3", lg: "8 / -1" }}
          row={{ sm: "5", md: "5", lg: "4" }}
        >
          <Img
            fluid={query.gridImage.childImageSharp.fluid}
            alt="Gallery of images at various locations"
          />
        </Block>
        <Block 
          col={{ sm: "1 / -1", md: "2 / -2", lg: "2 / 7" }}
          row={{ sm: "6", md: "6", lg: "4" }}
          variants={[
            css`
              margin-top: 48px;
              text-align: center;
            `,
            css``,
            css`
              margin-top: 0px;
              text-align: left;
              align-self: center;
            `
          ]}
        >
          <ContentBlock.H2
            title="Who Needs Indoor Positioning Systems?"
            description="Shopping malls, offices, hospitals, retailers, warehouses, and more can benefit from indoor positioning. These industries can provide an enhanced blue dot experience that mirrors the one offered in outdoor environments."
            bodyVariants={[
              css`
                color: var(--body-text-grey);;
              `,
            ]}
          />
          <Button.ScrollLink to="contact-form" smooth duration={1000}
            variants={[
                css`
                    margin: 32px auto 0 auto;
                `,
                css``,
                css`
                    margin: 48px 0 0 0;
                `
            ]}
          >
            Book A Demo
          </Button.ScrollLink>
        </Block>

        {/* Positioning vs mapping */}
        <Spacer 
          height={{ sm: "64px", md: "96px", lg: "64px" }}
          row={{ sm: "7", md: "7", lg: "5" }}
        />
        <Block 
          col={{ sm: "1 / -1", md: "1 / 7", lg: "1 / 6" }}
          row={{ sm: "8", md: "9", lg: "6" }}
          variants={[
            css``,
            css`
              margin-top: 52px;
            `,
            css`
              margin-top: 0px;
            `
          ]}
        >
          <Img
            fluid={query.positioningImage.childImageSharp.fluid}
            alt="Positioning Image"
          />
        </Block>
        <Block 
          col={{ sm: "1 / -1", md: "2 / -2", lg: "7 / 12" }}
          row={{ sm: "9", md: "8", lg: "6" }}
          variants={[
            css`
              text-align: center;
              margin-top: 32px;
            `,
            css`
              margin-top: 0px;
            `,
            css`
              text-align: left;
              align-self: center;
              z-index: 1;
            `
          ]}
        >
          <ContentBlock.H2
            title={
              <>
                Indoor Positioning <span>vs.</span> Indoor Mapping
              </>
            }
            description="It is important to understand that indoor positioning and indoor mapping are different but complementary services. By integrating IPS with a digital map, businesses can provide enhanced mapping features including custom starting location, turn-by-turn directions, follow mode, proximity messaging, and more. However, indoor positioning without an indoor map is rendered nearly useless for any visualization purposes."
            titleVariants={[
              css`
                & > span {
                  color: var(--burnt-orange);
                }
              `,
            ]}
            bodyVariants={[
              css`
                color: var(--body-text-grey);
              `,
            ]}
          />
        </Block>
        <Block 
          col={{ sm: "1 / -1", md: "7 / -1", lg: "7 / 12" }}  
          row={{ sm: "10", md: "9", lg: "7" }}
          variants={[
            css`
              margin-top: 48px;
            `,
            css`
            
            `,
            css`
              margin-top: -16px;
              z-index: 0;
            `
          ]}
        >
          <Img
            fluid={query.mappingImage.childImageSharp.fluid}
            alt="Mapping Image"
          />
        </Block>
        <Block 
          col={{ sm: "1 / -1", md: "2 / -2", lg: "2 / 6" }}
          row={{ sm: "11", md: "10", lg: "7" }}
          variants={[
            css`
              margin-top: 16px; // already 16px margin in description 
              text-align: center;
            `,
            css`
            `,
            css`
              text-align: left;
              align-self: center;
            `
          ]}
        >
          <ContentBlock.H2
            description="The key difference between these two technologies is that indoor mapping helps to visualize an indoor space, while indoor positioning locates a user's current real-time position within a particular and defined map. In short, venues can have an indoor map without indoor positioning, but not the other way around."
            bodyVariants={[
              css`
                color: var(--body-text-grey);
              `,
            ]}
          />
          <Button.ScrollLink to="contact-form" smooth duration={1000}
            variants={[
                css`
                    margin: 32px auto 0 auto;
                `,
                css``,
                css`
                    margin: 48px 0 0 0;
                `
            ]}
          >
            Book A Demo
          </Button.ScrollLink>
        </Block>
        <Spacer 
          height={{ sm: "48px", md: "48px", lg: "64px" }}
          row={{ sm: "12", md: "11", lg: "8" }}
        />
      </Grid>
    </>
  )
}
